'use client';

// `/login` клиентская форма — адаптация `/srv/sintra/frontend/app/(public)/login/page.tsx`
// под cookie/BFF-модель (ARCH-D-028, контракт §7.2 таблица переноса, §7.6 состояния UI,
// auth.cs.md §11.1).
//
// Адаптации vs эталон:
//   • `useAuth()` — из адаптированного `lib/auth/auth-store` (cookie-модель, без localStorage);
//   • login-вызов через `useAuth().login` → `POST /api/auth/login` (own-origin, без Authorization);
//   • чекбокс «Запомнить меня» задаёт TTL refresh-cookie: 30 дней с галочкой («как у
//     Microsoft»), 1 день без (безопаснее на публичных ПК). Режим сохраняется при
//     ротации через claim `rem` (fix-auth-session-lifetime; решение 9 SPEC_CHANGELOG superseded);
//   • серверный guard cold-open вынесен в RSC `page.tsx` (форма — только неавторизованная ветка);
//   • loading-state `Вход...` на кнопке (контракт §7.6);
//   • `INVALID_CREDENTIALS` → inline Alert `Неверный email или пароль`;
//   • `RATE_LIMITED` → inline Alert `Слишком много попыток. Повторите позже.`;
//   • поле password не имеет встроенной валидации (политика делегирована Frappe, §2.1).
//
// Визуальная система (gradient/panel/typography/spacing) — as-is из эталона (Mantine, theme tokens).

import { useState } from 'react';
import type { CSSProperties } from 'react';
import Link from 'next/link';
import {
  Alert,
  Badge,
  TextInput,
  PasswordInput,
  Checkbox,
  Button,
  Text,
  Box,
  Stack,
  Group,
  Paper,
  Container,
  Divider,
  Title,
  Anchor,
  useMantineTheme,
} from '@mantine/core';
import { useAuth } from '@/lib/auth/auth-store';
import { PortalApiError } from '@/lib/api/errors';
import { useStableColorScheme } from '@/lib/use-stable-color-scheme';
import styles from '../auth-page.module.css';

export interface LoginFormProps {
  /** RSC guard уже проверил сессию — форма сразу показывается (нет checkingSession-step). */
  initialSessionChecked: boolean;
}

export default function LoginForm({ initialSessionChecked }: LoginFormProps) {
  const theme = useMantineTheme();
  const colorScheme = useStableColorScheme();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', remember: false });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // initialSessionChecked — гарантия RSC-обёртки: сессия уже проверена, форма сразу видима.
  void initialSessionChecked;

  const isDark = colorScheme === 'dark';
  const pageGradient = isDark ? theme.other.pageGradientDark : theme.other.pageGradientLight;
  const panelColor = isDark ? theme.other.panelColorDark : theme.other.panelColorLight;
  const lineColor = isDark ? theme.other.lineColorDark : theme.other.lineColorLight;
  const mutedColor = isDark ? '#b7abd7' : '#6e6790';

  const authVars = {
    '--auth-page-gradient': pageGradient,
    '--auth-panel-bg': panelColor,
    '--auth-panel-strong': isDark ? theme.other.panelStrongColorDark : theme.other.panelStrongColorLight,
    '--auth-line-color': lineColor,
    '--auth-muted-color': mutedColor,
    '--auth-accent-start': theme.other.brandAccentStart,
    '--auth-accent-end': theme.other.brandAccentEnd,
    '--auth-brand-shadow': theme.other.brandButtonShadow,
    '--auth-control-radius': theme.other.controlBorderRadius,
  } as CSSProperties;

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Пожалуйста, введите электронную почту';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Пожалуйста, введите корректный адрес электронной почты';
    }
    if (!formData.password) {
      newErrors.password = 'Пожалуйста, введите пароль';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setSubmitError(null);
    try {
      // useAuth().login сам делает setUser + router.push('/news') (NAV-CONT-D-010).
      await login({
        email: formData.email,
        password: formData.password,
        remember: formData.remember,
      });
    } catch (error) {
      if (error instanceof PortalApiError) {
        if (error.code === 'VALIDATION_ERROR' && error.fields) {
          setErrors(error.fields);
        }
        // Единое сообщение INVALID_CREDENTIALS (§2.2) — не раскрывает существование аккаунта.
        setSubmitError(error.message || 'Ошибка входа');
      } else if (error instanceof Error) {
        setSubmitError('Произошла ошибка. Попробуйте снова.');
      } else {
        setSubmitError('Произошла ошибка. Попробуйте снова.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="main"
      className={styles.pageWrapper}
      style={{ background: pageGradient, ...authVars }}
      suppressHydrationWarning
    >
      <Box className={styles.mainContent} px={{ base: 'md', sm: 28 }} py={{ base: 32, sm: 48 }}>
        <Container size={460} px={0}>
          <Paper
            className={styles.card}
            radius={theme.other.panelBorderRadius}
            px={{ base: 20, sm: 36 }}
            py={{ base: 24, sm: 36 }}
            withBorder
            shadow="lg"
            style={{
              borderWidth: '1px',
              background: panelColor,
              borderColor: lineColor,
              backdropFilter: 'blur(14px)',
            }}
            suppressHydrationWarning
          >
            <Stack gap="lg">
              <Badge variant="light" className={styles.eyebrow} size="lg" radius="xl">
                Личный кабинет
              </Badge>

              <Title order={1} size="h2" fw={800} ta="center" className={styles.title}>
                Вход в систему
              </Title>

              <Text
                size="sm"
                c={mutedColor}
                ta="center"
                className={styles.description}
                mx="auto"
              >
                Войдите в портал, чтобы работать с документами, обучением и внутренними сервисами в едином интерфейсе.
              </Text>

              <form onSubmit={handleSubmit}>
                <Stack gap="lg">
                  {submitError && (
                    <Alert color="red" radius="md" variant="light">
                      {submitError}
                    </Alert>
                  )}

                  <TextInput
                    label="Электронная почта"
                    placeholder="example@company.ru"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    error={errors.email}
                    required
                    disabled={isLoading}
                  />

                  <PasswordInput
                    label="Пароль"
                    placeholder="Введите ваш пароль"
                    name="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    error={errors.password}
                    required
                    disabled={isLoading}
                  />

                  <Group justify="space-between" align="center" gap="sm" wrap="nowrap">
                    <Checkbox
                      label="Запомнить меня"
                      checked={formData.remember}
                      onChange={(e) =>
                        setFormData({ ...formData, remember: e.currentTarget.checked })
                      }
                      size="sm"
                      disabled={isLoading}
                      c={mutedColor}
                    />
                    <Anchor component={Link} href="/forgot-password" c="violet" size="sm" fw={600}>
                      Забыли пароль?
                    </Anchor>
                  </Group>

                  <Button
                    type="submit"
                    fullWidth
                    loading={isLoading}
                    mt={8}
                    variant="gradient"
                    className={styles.submitButton}
                  >
                    {isLoading ? 'Вход...' : 'Войти в систему'}
                  </Button>
                </Stack>
              </form>

              <Divider
                label={<Text size="13px" c={mutedColor}>Нет аккаунта?</Text>}
                labelPosition="center"
                my={24}
              />

              <Text ta="center" size="14px">
                <Anchor component={Link} href="/register" c="violet" fw={600} td="none">
                  Создать аккаунт
                </Anchor>
              </Text>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
